import { ActionIcon, Badge, Button, Group, Modal, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconDeviceFloppy, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useVariables, Variable } from '../hooks/useVariables';

export function VariablesPanel() {
  const { variables, loading, createVariable, updateVariable, deleteVariable } = useVariables();
  
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableValue, setNewVariableValue] = useState('');
  const [editingVariable, setEditingVariable] = useState<Variable | null>(null);
  
  // Mở modal thêm biến mới
  const openAddModal = () => {
    setNewVariableName('');
    setNewVariableValue('');
    setAddModalOpen(true);
  };
  
  // Mở modal chỉnh sửa biến
  const openEditModal = (variable: Variable) => {
    setEditingVariable(variable);
    setNewVariableName(variable.name);
    setNewVariableValue(variable.value);
    setEditModalOpen(true);
  };
  
  // Đóng các modal
  const closeModals = () => {
    setAddModalOpen(false);
    setEditModalOpen(false);
    setEditingVariable(null);
  };
  
  // Xử lý thêm biến mới
  const handleAddVariable = async () => {
    if (newVariableName.trim()) {
      await createVariable(newVariableName.trim(), newVariableValue);
      closeModals();
    }
  };
  
  // Xử lý cập nhật biến
  const handleUpdateVariable = async () => {
    if (editingVariable && newVariableName.trim()) {
      await updateVariable(editingVariable.name, {
        name: newVariableName.trim(),
        value: newVariableValue
      });
      closeModals();
    }
  };
  
  // Xử lý xóa biến
  const handleDeleteVariable = async (name: string) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa biến này?')) {
      await deleteVariable(name);
    }
  };
  
  return (
    <>
      <Group justify="space-between" mb="xs">
        <Title order={6}>Danh sách biến</Title>
      </Group>
      
      <Stack gap="xs">
        {variables.length === 0 ? (
          <Text color="dimmed" size="sm" ta="center">
            {loading ? 'Đang tải...' : 'Chưa có biến nào'}
          </Text>
        ) : (
          <Stack gap={5}>
            {variables.map((variable) => (
              <Group key={variable.name} justify="space-between" p="xs" bg="gray.0" style={{ borderRadius: 4 }}>
                <Group gap="xs" wrap="nowrap">
                  <Badge color="blue" variant="filled" size="sm">{variable.name}</Badge>
                  <Text size="xs" color="dimmed" truncate style={{ maxWidth: 120 }}>
                    {variable.value || '(trống)'}
                  </Text>
                </Group>
                <Group gap={4} wrap="nowrap">
                  <ActionIcon 
                    size="xs" 
                    color="blue" 
                    onClick={() => openEditModal(variable)}
                  >
                    <IconEdit size={14} />
                  </ActionIcon>
                  <ActionIcon 
                    size="xs" 
                    color="red" 
                    onClick={() => handleDeleteVariable(variable.name)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              </Group>
            ))}
          </Stack>
        )}
        
        <Group mt="xs">
          <Button 
            size="xs" 
            leftSection={<IconPlus size={12} />}
            onClick={openAddModal}
            variant="outline"
            fullWidth
          >
            Thêm biến mới
          </Button>
        </Group>
      </Stack>
      
      {/* Modal thêm biến mới */}
      <Modal
        opened={addModalOpen}
        onClose={closeModals}
        title="Thêm biến mới"
        size="sm"
      >
        <Stack>
          <TextInput
            label="Tên biến"
            placeholder="Nhập tên biến"
            value={newVariableName}
            onChange={(e) => setNewVariableName(e.target.value)}
            required
          />
          <TextInput
            label="Giá trị"
            placeholder="Nhập giá trị"
            value={newVariableValue}
            onChange={(e) => setNewVariableValue(e.target.value)}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={closeModals}>Hủy</Button>
            <Button 
              leftSection={<IconPlus size={14} />}
              onClick={handleAddVariable}
              disabled={!newVariableName.trim()}
            >
              Thêm biến
            </Button>
          </Group>
        </Stack>
      </Modal>
      
      {/* Modal chỉnh sửa biến */}
      <Modal
        opened={editModalOpen}
        onClose={closeModals}
        title="Chỉnh sửa biến"
        size="sm"
      >
        <Stack>
          <TextInput
            label="Tên biến"
            placeholder="Nhập tên biến"
            value={newVariableName}
            onChange={(e) => setNewVariableName(e.target.value)}
            required
          />
          <TextInput
            label="Giá trị"
            placeholder="Nhập giá trị"
            value={newVariableValue}
            onChange={(e) => setNewVariableValue(e.target.value)}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={closeModals}>Hủy</Button>
            <Button 
              leftSection={<IconDeviceFloppy size={14} />}
              onClick={handleUpdateVariable}
              disabled={!newVariableName.trim()}
            >
              Lưu thay đổi
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
} 